import React from 'react'
import { inject, observer } from 'mobx-react'
import classnames from 'classnames'
import _ from 'lodash'
import NumberInput from 'components/common/NumberInput'
import MapInstruction from '../common/MapInstruction'
import ToolNote from '../common/ToolNote'
import ColorPicker from '../common/ColorPicker'
import { trackValue } from 'utils.js'
import { withTranslation } from 'react-i18next'
import { bufferAroundLine } from 'constants.js'
import PaneHeader from './PaneHeader'
import CustomDropdown from '../../common/CustomDropdown'

const LinePane = ({
  line, group, uiStore, dataStore, selectionStore, t
}) => {
  const handleEdit = () => {
    line.drawing = false
    line.wasDrawing = true
  }

  const handleConfirmAdd = () => {
    uiStore.addGeopolygon(group, line)
  }

  const handleCancel = () => {
    if (line.isUpdating) {
      selectionStore.select(_.find(group.geopolygons, { id: line.id }), { data: { group } }, false, true)
    } else {
      selectionStore.clear()
    }
  }

  const paneTitle = line.isUpdating
    ? t('ITEM.EDIT_LINE', { defaultValue: 'Edit line' })
    : t('ITEM.LINE_BUFFER_TITLE', { defaultValue: 'Line Buffer' })

  const submitBtnText = line.isUpdating
    ? t('GLOBAL.CONFIRM_SAVE', { defaultValue: 'Save line' })
    : t('ITEM.CREATE_LINE_BTN', { defaultValue: 'Create line' })

  return (
    <div className='line-pane'>
      <PaneHeader
        icon={bufferAroundLine}
        title={paneTitle}
        onClose={handleCancel}
      />

      <section>
        {
          line.drawing
            ? (
                line.points.length === 0
                  ? <MapInstruction>{t('ITEM.DRAW_LINE_START')}</MapInstruction>
                  : <MapInstruction>{t('ITEM.DRAW_LINE_CONTINUE')}</MapInstruction>
              )
            : null
        }
      </section>

      <form className='spaced-children' onSubmit={(e) => { e.preventDefault(); if (line.isUpdating) { uiStore.updateGeopolygon(group, line) } else { handleConfirmAdd() } }}>
        <section className='spaced-children'>
          <label>
            <span>{t('GLOBAL.NAME', { defaultValue: 'Name' })}</span>
            <input
              className='name-input'
              type='text'
              placeholder={t('ITEM.LINE_NAME_PLACEHOLDER', { defaultValue: 'e.g. Highway corridor' })}
              {...trackValue(line, 'name')}
              autoFocus
            />
            <span className='invalid'>{line.invalid.get('name')}</span>
          </label>

          <label>
            <span>{t('GLOBAL.BUFFER_DISTANCE', { defaultValue: 'Buffer distance' })}</span>
            <div className='input-with-unit'>
              <NumberInput
                extraClasses={['buffer-input']}
                min={1}
                max={1000}
                value={line.buffer}
                inputValue={line.buffer}
                onChange={value => (line.buffer = value)}
              />
              <CustomDropdown
                className='custom-dropdown--unit'
                value={line.bufferUnit}
                options={[
                  { value: 'km', label: t('GLOBAL.UNIT_KM', { defaultValue: 'km' }) },
                  { value: 'mile', label: t('GLOBAL.UNIT_MILE', { defaultValue: 'mile' }) }
                ]}
                onChange={value => (line.bufferUnit = value)}
              />
            </div>
            <span className='invalid'>{line.invalid.get('buffer')}</span>
          </label>

          {
            !line.isUpdating && (
              <div>
                <ColorPicker
                  onClick={(idx) => { line.color_id = idx }}
                  selectedId={line.color_id}
                  hasTransparencyOption
                  transparency={line.transparency}
                  onTransparencyChange={(val) => { line.transparency = val }}
                />
              </div>
            )
          }
        </section>

        <section>
          {
            line.tooLarge
              ? (
                <ToolNote type='warning'>
                  {t('ITEM.GEOMETRY_TOO_LARGE')}
                </ToolNote>
                )
              : (
                  line.largeEnoughToBeInaccurate
                    ? (
                      <ToolNote type='info'>
                        {t('ITEM.LARGE_BUFFER_INACCURACY')}
                      </ToolNote>
                      )
                    : null
                )
          }
          {
            line.drawing
              ? (
                <button
                  type='button'
                  disabled={line.points.length < 2}
                  className='finish-line'
                  onClick={handleEdit}
                >
                  <span>{t('ITEM.COMPLETE_LINE')}</span>
                </button>
                )
              : null
          }
          {
            line.wasDrawing
              ? (
                <button
                  type='button'
                  disabled={line.points.length < 2}
                  className='finish-line'
                  onClick={() => {
                    line.drawing = true
                    line.wasDrawing = false
                  }}
                >
                  <span>{t('ITEM.EDIT_LINE')}</span>
                </button>
                )
              : null
          }
        </section>

        <section>
          <div className='centered-button-row'>
            <button
              type='button'
              onClick={handleCancel}
            >
              <span>{t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}</span>
            </button>
            <button
              type='button'
              className={classnames({
                ok: true,
                'loading loading-item loading-dark': line.updatingRegion
              })}
              onClick={() => line.isUpdating
                ? uiStore.updateGeopolygon(group, line)
                : handleConfirmAdd()}
              disabled={line.updatingRegion || line.drawing || line.tooLarge}
            >
              <span>{submitBtnText}</span>
            </button>
          </div>
        </section>
      </form>
    </div>
  )
}

export default withTranslation()(inject('uiStore', 'dataStore', 'selectionStore')(observer(LinePane)))
